/**
 * Area — Stato richiesta (PR-ONB-RESUME-1B, locks #3/#6).
 *
 * Shows the request's effective status, the last-save stamp, the email
 * verification banner with the "Ho verificato l'email" CTA (lock #3:
 * firebaseUser.reload() → getIdToken(true) → fresh API call so the BE
 * persists email_verified_at), the applicant-side withdraw and logout.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner } from '@ab-platform/ui/banner';
import { FieldError } from '@ab-platform/ui/field-error';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { PillButton } from '@ab-platform/ui/pill-button';
import { SoftBadge } from '@ab-platform/ui/soft-badge';
import { useAreaSession } from '@/hooks/AreaSessionContext';
import { useAreaMe } from '@/pages/area/areaMeContext';
import {
  getAreaRequest,
  withdrawAreaRequest,
  type AreaOpenIssue,
} from '@/lib/areaApi';
import { areaStatusView, canWithdraw } from '@/lib/areaStatusView';
import {
  issueReasonLabel,
  issueTargetLabel,
} from '@/lib/correctionIssuesView';
import { buildEmailVerificationBanner } from '@/lib/emailVerificationView';
import { performAreaEmailRefresh } from '@/lib/areaEmailRefreshFlow';

function formatStamp(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AreaStatusPage(): JSX.Element {
  const session = useAreaSession();
  const navigate = useNavigate();
  const { me, refresh } = useAreaMe();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifyChecked, setVerifyChecked] = useState(false);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  // Resend cooldown (review fix #7): client-side anti-spam; Firebase has its
  // own server-side throttle (auth/too-many-requests) mapped below.
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'cooldown'>('idle');

  const request = me.request;
  const view = request ? areaStatusView(request.effectiveStatus) : null;
  const lastSaved = request ? formatStamp(request.lastSavedAt) : null;
  // No proof a verification email was actually sent at load time → neutral copy
  // (the resend CTA below reports its own honest outcome).
  const emailBanner = buildEmailVerificationBanner({
    emailVerified: me.account.emailVerified,
    sendOutcome: 'unknown',
  });

  // PR-ONB-PRECHECK-3 — on NEEDS_CORRECTION the summary alone is not enough:
  // fetch the full request view once to list the OPEN structured issues.
  const [openIssues, setOpenIssues] = useState<AreaOpenIssue[]>([]);
  const needsCorrection = request?.status === 'NEEDS_CORRECTION';
  useEffect(() => {
    if (!needsCorrection) {
      setOpenIssues([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const token = await session.getToken();
      if (!token) return;
      const result = await getAreaRequest(token);
      if (!cancelled && result.ok) setOpenIssues(result.data.openIssues);
    })();
    return () => {
      cancelled = true;
    };
  }, [needsCorrection, session]);

  // PR-ONB-WIZARD-AREA-UX-1 — auto-propagate a freshly-verified email on area
  // entry: if the email is not yet verified BE-side, run reloadUser →
  // getIdToken(true) → /me ONCE on mount, so the user doesn't have to click
  // "Ho verificato l'email" (or re-login) for the stamp to land. Ref-guarded so
  // the refresh()-driven re-render can't loop.
  const autoEmailRefreshedRef = useRef(false);
  useEffect(() => {
    if (autoEmailRefreshedRef.current) return;
    if (me.account.emailVerified) return;
    autoEmailRefreshedRef.current = true;
    void performAreaEmailRefresh({
      reloadUser: () => session.reloadUser(),
      getToken: (force) => session.getToken(force),
      refresh,
    });
  }, [me.account.emailVerified, refresh, session]);

  // Lock #3 — "Ho verificato l'email": reload the Firebase user, force a
  // fresh ID token (carries the updated claim) and re-call the API so the
  // middleware persists email_verified_at.
  const handleEmailVerifiedCheck = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await session.reloadUser();
      await session.getToken(true);
      refresh();
      setVerifyChecked(true);
    } catch {
      setError('Controllo non riuscito. Riprova tra qualche istante.');
    } finally {
      setBusy(false);
    }
  };

  const RESEND_COOLDOWN_MS = 60_000;

  const handleResendVerification = async (): Promise<void> => {
    setError(null);
    setResendState('sending');
    try {
      const fb = await import('@/lib/firebaseClient');
      const current = fb.getFirebaseAuth().currentUser;
      if (!current) {
        setError('Sessione scaduta: accedi di nuovo.');
        setResendState('idle');
        return;
      }
      await fb.sendVerificationEmail(current);
      setResendState('cooldown');
      setTimeout(() => setResendState('idle'), RESEND_COOLDOWN_MS);
    } catch (err) {
      const code = (err as { code?: string }).code ?? '';
      setError(
        code === 'auth/too-many-requests'
          ? 'Troppi invii ravvicinati. Attendi qualche minuto e riprova.'
          : 'Invio non riuscito. Riprova tra qualche istante.',
      );
      setResendState('idle');
    }
  };

  const handleWithdraw = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const token = await session.getToken();
      if (!token) {
        setError('Sessione scaduta: accedi di nuovo.');
        return;
      }
      const result = await withdrawAreaRequest(token);
      if (!result.ok) {
        setError('Ritiro non riuscito. Riprova.');
        return;
      }
      setConfirmWithdraw(false);
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async (): Promise<void> => {
    await session.signOutArea();
    navigate('/area/login');
  };

  return (
    <FocusedPageShell
      title="Stato richiesta"
      description={`Account: ${me.account.email}`}
      footer={
        <PillButton
          type="button"
          size="lg"
          variant="outline"
          className="w-full"
          onClick={() => void handleLogout()}
        >
          Esci
        </PillButton>
      }
    >
      <div className="grid gap-4">
        {!me.account.emailVerified ? (
          <Banner tone={emailBanner.tone} title={emailBanner.title}>
            <div className="grid gap-2">
              <span>{emailBanner.message}</span>
              <div className="flex flex-wrap gap-2">
                <PillButton
                  type="button"
                  size="sm"
                  disabled={busy}
                  onClick={() => void handleEmailVerifiedCheck()}
                >
                  Ho verificato l'email
                </PillButton>
                <PillButton
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={resendState !== 'idle'}
                  onClick={() => void handleResendVerification()}
                >
                  {resendState === 'sending'
                    ? 'Invio…'
                    : resendState === 'cooldown'
                      ? 'Email inviata'
                      : 'Invia nuovamente l\'email'}
                </PillButton>
              </div>
              {verifyChecked ? (
                <span className="text-xs text-muted-foreground">
                  Controllo eseguito. Se hai appena cliccato il link, lo stato
                  si aggiornerà tra pochi istanti.
                </span>
              ) : null}
            </div>
          </Banner>
        ) : (
          <Banner tone="success" title="Email verificata">
            L'indirizzo {me.account.email} è verificato.
          </Banner>
        )}

        {request && view ? (
          <div className="grid gap-2 rounded-2xl border border-border p-4">
            <div className="flex items-center gap-2">
              <SoftBadge
                tone={view.tone === 'neutral' ? 'neutral' : view.tone}
              >
                {view.label}
              </SoftBadge>
              {lastSaved ? (
                <span className="text-xs text-muted-foreground">
                  Ultimo salvataggio: {lastSaved}
                </span>
              ) : null}
            </div>
            <p className="text-sm text-muted-foreground">{view.description}</p>
          </div>
        ) : (
          <Banner tone="info" title="Nessuna richiesta attiva">
            Non hai una richiesta in corso. Riparti dalla sezione Dati.
          </Banner>
        )}

        {request?.status === 'PREAUTHORIZED_FOR_IDENTITY' ? (
          <Banner tone="success" title="Controllo preliminare completato">
            <div className="grid gap-2">
              <span>Ora verifica la tua identità per proseguire.</span>
              <div>
                <PillButton
                  type="button"
                  size="sm"
                  onClick={() => navigate('/area/verifica-identita')}
                >
                  Verifica la tua identità
                </PillButton>
              </div>
            </div>
          </Banner>
        ) : null}

        {needsCorrection && openIssues.length > 0 ? (
          <Banner tone="warning" title="Cosa va corretto">
            <div className="grid gap-2">
              <ul className="grid list-disc gap-1 pl-5">
                {openIssues.map((issue) => (
                  <li key={issue.id} className="text-sm">
                    <strong>{issueTargetLabel(issue.targetKey)}</strong> —{' '}
                    {issueReasonLabel(issue.reasonCode)}
                    {issue.note ? <>: {issue.note}</> : null}
                  </li>
                ))}
              </ul>
              <div>
                <PillButton
                  type="button"
                  size="sm"
                  onClick={() => navigate('/area/dati')}
                >
                  Correggi i dati
                </PillButton>
              </div>
            </div>
          </Banner>
        ) : null}

        {request && canWithdraw(request.status) ? (
          <div className="grid gap-2">
            {confirmWithdraw ? (
              <div className="grid gap-2 rounded-2xl border border-border p-4">
                <p className="text-sm">
                  Confermi di voler ritirare la richiesta? Potrai aprirne una
                  nuova in seguito.
                </p>
                <div className="flex gap-2">
                  <PillButton
                    type="button"
                    size="sm"
                    disabled={busy}
                    onClick={() => void handleWithdraw()}
                  >
                    Conferma ritiro
                  </PillButton>
                  <PillButton
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmWithdraw(false)}
                  >
                    Annulla
                  </PillButton>
                </div>
              </div>
            ) : (
              <div>
                <PillButton
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmWithdraw(true)}
                >
                  Ritira la richiesta
                </PillButton>
              </div>
            )}
          </div>
        ) : null}

        {error ? <FieldError message={error} /> : null}
      </div>
    </FocusedPageShell>
  );
}
